<form
  [signalForm]="configForm"
  class="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-4"
>
  <section class="flex flex-col gap-3">
    <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-400">
      Field Settings
    </h3>
    <lib-signal-text-field
      label="Label"
      placeholder="Multi Choice Grid"
      [formField]="configForm.label"
    />
    <lib-signal-text-field
      label="Description"
      placeholder="Select all options that apply."
      [formField]="configForm.helpText"
    />
  </section>
  <section class="flex flex-col gap-3">
    <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-400">
      Response Type
    </h3>

    <div class="grid grid-cols-3 gap-2">
      @for (choice of responseTypeChoices; track choice.value) {
        <button
          type="button"
          class="rounded-xl border px-3 py-3 text-sm font-medium transition"
          [class.border-blue-500]="effectiveResponseType() === choice.value"
          [class.bg-blue-50]="effectiveResponseType() === choice.value"
          [class.text-blue-700]="effectiveResponseType() === choice.value"
          [class.border-slate-200]="effectiveResponseType() !== choice.value"
          [class.bg-white]="effectiveResponseType() !== choice.value"
          [class.text-slate-600]="effectiveResponseType() !== choice.value"
          (click)="setResponseType(choice.value)"
        >
          {{ choice.label }}
        </button>
      }
    </div>
    @if (previewOptions().length > 0) {
      <div class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
        <p
          class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400"
        >
          Preview
        </p>
        @switch (effectiveResponseType()) {
          @case ('checkbox') {
            <div class="flex flex-wrap gap-3">
              @for (option of previewOptions(); track $index) {
                <label class="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    class="h-4 w-4 accent-blue-600"
                    [checked]="$first"
                    disabled
                  />
                  {{ option }}
                </label>
              }
            </div>
          }
          @case ('dropdown') {
            <div
              class="flex items-center justify-between rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
            >
              <span>{{ previewOptions()[0] }}</span>
              <app-svg-icon [size]="8" [src]="'assets/icons/downward.svg'"></app-svg-icon>
            </div>
          }
          @case ('button') {
            <div class="flex flex-wrap gap-2">
              @for (option of previewOptions(); track $index) {
                <span
                  class="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700"
                >
                  {{ option }}
                </span>
              }
            </div>
          }
        }
      </div>
    }
  </section>

  <section class="flex flex-col gap-3">
    <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-400">
      Response Options
    </h3>

    <div
      class="flex flex-col gap-2"
      cdkDropList
      (cdkDropListDropped)="onOptionDrop($event)"
    >
      @for (option of model().options; track option.id; let i = $index) {
        <div class="flex items-center gap-2" cdkDrag>
          <button
            type="button"
            cdkDragHandle
            class="inline-flex h-8 w-8 shrink-0 cursor-grab items-center justify-center rounded-lg border-0 bg-transparent text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 active:cursor-grabbing"
            aria-label="Reorder response option"
          >
            <app-svg-icon
              src="assets/icons/drag-vertical.svg"
              [size]="18"
            />
          </button>
          <lib-signal-text-field
            class="min-w-0 flex-1"
            label=""
            size="sm"
            placeholder="Enter response option"
            [formField]="configForm.options[i].label"
          />
          <button
            type="button"
            class="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-slate-100 text-slate-500 transition hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
            title="Delete option"
            aria-label="Delete option"
            [disabled]="model().options.length <= 2"
            (click)="removeOption(i)"
          >
            <app-svg-icon
              src="assets/icons/delete-outline.svg"
              [size]="16"
            />
          </button>
          <div
            *cdkDragPlaceholder
            class="h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50"
          ></div>
        </div>
      }
    </div>
    <lib-signal-button
      type="button"
      appearance="outline"
      size="sm"
      [fullWidth]="true"
      (click)="addOption()"
    >
      Add response option
    </lib-signal-button>
  </section>
  <section class="flex flex-col gap-3">
    <div class="flex items-center justify-between gap-2">
      <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-400">
        Items (Rows)
      </h3>
      @if (!isCollectionLinked()) {
        <app-link-collection-button
          (collectionSelected)="onCollectionSelected($event)"
        />
      }
    </div>
    @if (linkedCollection(); as collection) {
      <div
        class="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2"
      >
        <app-svg-icon
          class="shrink-0 text-slate-500"
          src="assets/icons/database-outline.svg"
          [size]="16"
        />
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-medium text-slate-900">
            {{ collection.label || collection.key || 'Collection' }}
          </p>
          @if (showAsLabel()) {
            <p class="truncate text-xs text-slate-500">
              Display: {{ showAsLabel() }}
            </p>
          } @else if (collection.key) {
            <p class="truncate text-xs text-slate-500">
              {{ collection.key }}
            </p>
          }
        </div>
        <button
          type="button"
          class="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent text-primary-600 transition hover:bg-primary-50"
          title="Change column"
          aria-label="Change column"
          (click)="changeShowAsColumn()"
        >
          <app-svg-icon
            src="assets/icons/square-edit-outline.svg"
            [size]="16"
          />
        </button>
        <button
          type="button"
          class="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
          title="Detach"
          aria-label="Detach"
          (click)="detachLinkedCollection()"
        >
          <app-svg-icon
            src="assets/icons/link-variant-off.svg"
            [size]="16"
          />
        </button>
        <button
          type="button"
          class="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent text-slate-500 transition hover:bg-rose-50 hover:text-rose-600"
          title="Remove"
          aria-label="Remove"
          (click)="removeLinkedCollection()"
        >
          <app-svg-icon
            src="assets/icons/delete-outline.svg"
            [size]="16"
          />
        </button>
      </div>
    } @else {
      <div
        class="flex flex-col gap-2"
        cdkDropList
        (cdkDropListDropped)="onItemDrop($event)"
      >
        @for (item of model().items; track $index; let i = $index) {
          <div class="flex items-center gap-2" cdkDrag>
            <button
              type="button"
              cdkDragHandle
              class="inline-flex h-8 w-8 shrink-0 cursor-grab items-center justify-center rounded-lg border-0 bg-transparent text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 active:cursor-grabbing"
              aria-label="Reorder item"
            >
              <app-svg-icon
                src="assets/icons/drag-vertical.svg"
                [size]="18"
              />
            </button>
            <lib-signal-text-field
              class="min-w-0 flex-1"
              label=""
              size="sm"
              placeholder="Enter item"
              [formField]="configForm.items[i]"
            />
            <button
              type="button"
              class="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-slate-100 text-slate-500 transition hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
              title="Delete item"
              aria-label="Delete item"
              [disabled]="model().items.length <= 1"
              (click)="removeItem(i)"
            >
              <app-svg-icon
                src="assets/icons/delete-outline.svg"
                [size]="16"
              />
            </button>

            <div
              *cdkDragPlaceholder
              class="h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50"
            ></div>
          </div>
        }
      </div>
      <lib-signal-button
        type="button"
        appearance="outline"
        size="sm"
        [fullWidth]="true"
        (click)="addItem()"
      >
        Add item
      </lib-signal-button>
    }
  </section>
  <section class="flex flex-col gap-3">
    <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-400">
      Behavior
    </h3>
    <lib-signal-switch
      label="Allow respondents to add additional rows"
      [formField]="configForm.allowAdditionalRows"
    />
    @if (model().allowAdditionalRows) {
      <lib-signal-text-field
        label="Add row button label"
        placeholder="+ Add another item"
        [formField]="configForm.addRowButtonLabel"
      />
    }
  </section>
  <lib-signal-checkbox
    label="Required"
    hint="Respondents must complete this field."
    [formField]="configForm.required"
  />
</form>
